import { appStoreConstant } from "@/utils/branch"
import clsx from "clsx"
import { useEffect, useRef } from "react"
import { z } from "zod"
import Button, { ConditionalAnchor } from "../button"

const featureAiToolSchema = z.enum([
	"deform",
	"ai_photo",
	"ai_restyle",
	"text_to_image",
	"text_to_video",
	"ai_music_generator",
])

const SCROLL_SPEED = 0.8

interface ImageWithGlowProps {
	source: string
	label: string
}

function ImageWithLabel(props: ImageWithGlowProps) {
	const { source, label } = props

	return (
		<>
			<div className="flex w-[125px] shrink-0 flex-col gap-[7px] tablet:w-[169px] desktop:w-[174px]">
				<img
					src={source}
					alt={label}
					className="h-[201px] w-full rounded-[14px] object-cover tablet:h-[272px] desktop:h-[280px]"
				/>
				<p className="body-4">{label}</p>
			</div>
		</>
	)
}

export const usecasesSchema = z.object({
	component: z.literal("usecases"),
	title: z.string(),
	subtitle: z.string().optional(),
	button_text: z.string().optional(),
	button_link: z.string().optional(),
	labels: z.array(
		z.object({ name: z.string(), thumb: z.string() }),
	),
	upload: z.boolean().optional(),
	tool: featureAiToolSchema.optional(),
})

type UsecasesProps = z.infer<typeof usecasesSchema>

export function Usecases(props: UsecasesProps) {
	const {
		labels,
		title,
		subtitle,
		button_text,
		button_link,
	} = props

	const samplesRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		const element = samplesRef.current
		if (!element) {
			return
		}

		function handleWheel(event: WheelEvent) {
			if (!element) {
				return
			}

			const isAtRightEdge =
				element.scrollLeft + element.clientWidth >=
				element.scrollWidth
			const isAtLeftEdge = element.scrollLeft === 0

			if (isAtRightEdge && event.deltaY > 0) {
				return
			}

			if (isAtLeftEdge && event.deltaY < 0) {
				return
			}

			event.preventDefault()

			element.scrollLeft +=
				event.deltaY * SCROLL_SPEED + event.deltaX
		}

		element.addEventListener("wheel", handleWheel, {
			passive: false,
		})

		return () =>
			element.removeEventListener("wheel", handleWheel)
	}, [samplesRef])

	return (
		<section
			className={clsx(
				"flex flex-col items-center justify-center",
				"py-[16px] tablet:py-[40px] desktop:py-[55px]",
			)}>
			<div className="mb-6 flex flex-col gap-2 tablet:mb-9 tablet:w-[605px] tablet:gap-4 desktop:w-[875px] desktop:gap-[22px]">
				<h3 className="text-h3 max-w-[400px] self-center px-4 text-center tablet:p-0 desktop:max-w-none">
					{title}
				</h3>
				<span className="text-body-2 hidden max-w-[800px] text-center tablet:block">
					{subtitle}
				</span>
			</div>

			<div
				className={clsx(
					"no-scrollbar w-full touch-pan-x overflow-y-hidden overflow-x-scroll",
					labels.length < 5 &&
						"flex tablet:items-center tablet:justify-center",
				)}
				ref={samplesRef}>
				<div
					className={clsx(
						"flex touch-pan-x gap-2 px-4 tablet:gap-3",
					)}>
					<div className="h-10 w-[calc(50vw-500px)] shrink-0" />
					{labels.map((style, index) => (
						<ImageWithLabel
							key={index}
							source={style.thumb}
							label={style.name.toUpperCase()}
						/>
					))}
					<div className="h-10 w-[calc(50vw-500px)] min-w-[16px] shrink-0" />
				</div>
			</div>
			<span className="text-body-2 mt-6 w-full max-w-[800px] px-4 text-center tablet:mt-9 tablet:hidden tablet:px-0">
				{subtitle}
			</span>

			{button_text && (
				<>
					<Button
						href={button_link}
						className="mt-8 hidden max-w-[250px] desktop:block"
						text={button_text}
						primary
					/>
					<div className="flex w-full items-center justify-center px-4">
						<ConditionalAnchor
							href={appStoreConstant}
							className=" w-full tablet:mt-9 tablet:w-[250px] desktop:hidden">
							{button_text}
						</ConditionalAnchor>
					</div>
				</>
			)}
		</section>
	)
}
